import { useEffect, useRef, useState } from "react";
import { BadgeDollarSign, BriefcaseBusiness, Code2, Scale } from "lucide-react";

const STAKEHOLDERS = [
  {
    id: "executive",
    label: "Executive",
    decision: "Is this priority worth funding and sponsoring now?",
    objection: "The business case is vague, or the change seems disconnected from a material business priority.",
    evidence: "Approved problem statement, decision owner, operating constraint, and a bounded case for change.",
    boundary: "Do not promise financial impact that has not been measured or approved.",
    nextStep: "Agree the decision to be made, the evidence required, and the accountable sponsor.",
    icon: BriefcaseBusiness,
    accent: "#31c4a4",
  },
  {
    id: "commercial",
    label: "Commercial",
    decision: "Will this improve the way the team reaches, qualifies, or progresses the right accounts?",
    objection: "The message describes features but not a relevant commercial problem or trade-off.",
    evidence: "Approved customer problem, relevant use case, offer scope, and a realistic implementation boundary.",
    boundary: "Do not turn engagement signals into a revenue or pipeline claim without a defined method.",
    nextStep: "Match one useful asset and one next step to the buyer stage being addressed.",
    icon: BadgeDollarSign,
    accent: "#60a5fa",
  },
  {
    id: "technical",
    label: "Technical",
    decision: "Can this work safely within the existing data, systems, and operating constraints?",
    objection: "The recommendation lacks an integration, security, ownership, or review plan.",
    evidence: "Approved architecture context, source limitations, security review inputs, and named technical owners.",
    boundary: "Do not make compatibility, security, or performance assertions without reviewed evidence.",
    nextStep: "Document assumptions, dependencies, escalation paths, and the human approval point.",
    icon: Code2,
    accent: "#a78bfa",
  },
  {
    id: "procurement",
    label: "Compliance & procurement",
    decision: "Are the data, contractual, governance, and accountability conditions clear enough to proceed?",
    objection: "The proposal cannot explain purpose, permissions, data handling, supplier responsibility, or exit conditions.",
    evidence: "Approved data-use boundaries, policy and contract inputs, supplier documentation, and an accountable reviewer.",
    boundary: "Do not infer individual information or present this illustration as legal, privacy, or compliance advice.",
    nextStep: "Confirm the purpose, minimum necessary data, approver, and evidence still needed before activation.",
    icon: Scale,
    accent: "#fbbf24",
  },
] as const;

export default function BuyerArgumentMatrix() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeStakeholder, setActiveStakeholder] = useState("executive");
  const active = STAKEHOLDERS.find((stakeholder) => stakeholder.id === activeStakeholder) ?? STAKEHOLDERS[0];

  useEffect(() => {
    const target = sectionRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.16 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="buyer-argument-matrix-heading"
      className="not-prose container mx-auto mb-12 max-w-4xl px-4"
    >
      <div className="overflow-hidden rounded-2xl border border-slate-700/80 bg-[#0d1f2d] text-white shadow-[0_18px_70px_rgba(7,22,35,0.22)]">
        <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(49,196,164,0.18),transparent_42%),linear-gradient(120deg,#102b3e,#17253a)] px-5 py-7 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#72e9cf]">Illustrative working method</p>
          <h2 id="buyer-argument-matrix-heading" className="mt-2 max-w-3xl font-serif text-2xl leading-tight text-white sm:text-3xl">
            Buyer Argument Matrix: change the decision, not just the greeting
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-200 sm:text-base">
            One account can contain several legitimate decisions. This matrix is a review tool for making the evidence, claim boundary, and next step specific to the stakeholder. It is not a buyer-data model, legal opinion, or revenue forecast.
          </p>
        </div>

        <div className="p-5 sm:p-8">
          <div className="grid gap-3 sm:grid-cols-2" role="tablist" aria-label="Buyer argument matrix stakeholders">
            {STAKEHOLDERS.map((stakeholder, index) => {
              const Icon = stakeholder.icon;
              const isActive = stakeholder.id === activeStakeholder;
              return (
                <button
                  key={stakeholder.id}
                  id={`${stakeholder.id}-tab`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${stakeholder.id}-panel`}
                  onClick={() => setActiveStakeholder(stakeholder.id)}
                  onFocus={() => setActiveStakeholder(stakeholder.id)}
                  className={`group min-h-[132px] rounded-xl border p-4 text-left transition-[transform,border-color,background-color,box-shadow] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-none ${
                    isActive
                      ? "border-white/55 bg-white/[0.13] shadow-[0_0_0_1px_rgba(255,255,255,0.08)]"
                      : "border-white/10 bg-white/[0.04] hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.08]"
                  }`}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? undefined : "translateY(12px)",
                    transitionDelay: `${index * 75}ms`,
                  }}
                >
                  <Icon aria-hidden="true" className="h-5 w-5" style={{ color: stakeholder.accent }} />
                  <p className="mt-4 text-sm font-semibold text-white">{stakeholder.label}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-300">{stakeholder.decision}</p>
                </button>
              );
            })}
          </div>

          <div
            id={`${active.id}-panel`}
            role="tabpanel"
            aria-labelledby={`${active.id}-tab`}
            className="mt-5 rounded-xl border border-white/10 bg-slate-950/35 p-5 sm:p-6"
          >
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: active.accent }} aria-hidden="true" />
              <h3 className="font-serif text-xl text-white">{active.label} argument</h3>
            </div>
            <dl className="mt-5 grid gap-5 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-[#72e9cf]">Likely objection</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-slate-200">{active.objection}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-[#72e9cf]">Evidence to approve</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-slate-200">{active.evidence}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-[#72e9cf]">Claim boundary</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-slate-200">{active.boundary}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-[#72e9cf]">Appropriate next step</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-slate-200">{active.nextStep}</dd>
              </div>
            </dl>
          </div>

          <p className="mt-5 text-xs leading-relaxed text-slate-400">
            Use the matrix before generating variants. The final message should be reviewed against approved account evidence and the organization&apos;s own privacy, legal, and brand requirements.
          </p>
        </div>
      </div>
    </section>
  );
}
